import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserAccess } from '../../interfaces/state/entities/user-access.interface';
import { IUserAccessService } from '../../interfaces/services/user-access-service.interface';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserAccessService implements IUserAccessService {
  private http = inject(HttpClient);
  private apiUrl = environment.scitCurrentUserUrl;

  getUserAccess(): Observable<UserAccess> {
    return this.http.get<unknown>(this.apiUrl).pipe(
      map((response) => this.mapRecordToAccess(this.extractUserRecord(response)))
    );
  }

  private extractUserRecord(body: unknown): Record<string, unknown> | null {
    const record = this.asRecord(body);
    if (!record) {
      return null;
    }

    if (Array.isArray(record['users']) && record['users'].length > 0) {
      return this.asRecord(record['users'][0]);
    }

    if (Array.isArray(record['Users']) && record['Users'].length > 0) {
      return this.asRecord(record['Users'][0]);
    }

    return record;
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private rolesFromRecord(record: unknown): string[] {
    if (!record || typeof record !== 'object') {
      return [];
    }

    const o = record as Record<string, unknown>;
    if (Array.isArray(o['roles'])) {
      return (o['roles'] as unknown[]).map((r) => String(r));
    }

    const singleRole = o['role'] ?? o['Role'];
    if (singleRole != null && String(singleRole).trim() !== '') {
      return [String(singleRole)];
    }

    return [];
  }

  private mapRecordToAccess(record: Record<string, unknown> | null): UserAccess {
    const normalized = this.rolesFromRecord(record).map((r) => String(r).trim().toLowerCase());
    const userId = this.readString(record, ['userId', 'UserId', 'id', 'Id']);
    const email = this.readString(record, ['email', 'Email']);
    return {
      isDeveloper: normalized.includes('user') || normalized.includes('developer'),
      isSupport: normalized.includes('support'),
      isAdmin: normalized.includes('admin'),
      ...(userId ? { userId } : {}),
      ...(email ? { email } : {}),
    };
  }

  private readString(record: Record<string, unknown> | null, keys: string[]): string | undefined {
    if (!record) {
      return undefined;
    }
    for (const key of keys) {
      const value = record[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return undefined;
  }
}
